<script setup lang="ts">
import { MapTypeId } from '@libs/maps';
import { AppMapLayerSwitcher, AppCheckbox, AppDrawer } from '@libs/ui';

interface MapSettingsDrawerProps {
  searchByMovingMap: boolean;
  mapType: MapTypeId;
}

const props = defineProps<MapSettingsDrawerProps>();

interface MapSettingsDrawerEmits {
  (eventName: 'close'): void;
  (eventName: 'toggle-search-by-moving-map', active: boolean): void;
  (eventName: 'change-map-type', mapType: MapTypeId): void;
}

const emit = defineEmits<MapSettingsDrawerEmits>();

function handleMapChange(mapType: MapTypeId): void {
  if (mapType === props.mapType) {
    return;
  }

  emit('change-map-type', mapType);
}
</script>

<template>
  <AppDrawer :height="{ value: 300, unit: 'px' }" @close="emit('close')">
    <template #header>
      <div class="map-settings-drawer-header">
        {{ $t('planner.map_settings') }}
      </div>
    </template>
    <template #content>
      <div class="map-settings-drawer-content">
        <AppCheckbox
          theme="secondary"
          box-position="left"
          size="small"
          class="checkbox"
          :checked="props.searchByMovingMap"
          @change="
            emit('toggle-search-by-moving-map', !props.searchByMovingMap)
          "
        >
          {{ $t('planner.search_when_moving_the_map') }}
        </AppCheckbox>
        <AppMapLayerSwitcher
          :selected="
            props.mapType === MapTypeId.ROADMAP ? 'roadmap' : 'satellite'
          "
          :roadmap-text="$t('planner.standard_map')"
          :satellite-text="$t('planner.satellite_map')"
          class="map-layer-switcher"
          @roadmap-selected="handleMapChange(MapTypeId.ROADMAP)"
          @satellite-selected="handleMapChange(MapTypeId.SATELLITE)"
        />
      </div>
    </template>
  </AppDrawer>
</template>

<style scoped lang="scss">
.map-settings-drawer-header {
  @include font-title-label-m;
}

.map-settings-drawer-content {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  .checkbox {
    padding: var(--spacing-24) 0;
    border-bottom: 1px solid var(--color-neutral-30);
    width: 80%;

    display: flex;
    align-items: center;
    justify-content: center;
  }

  .map-layer-switcher {
    padding: var(--spacing-24) 0;
  }
}
</style>
